import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { zonedTimeToUtc } from "date-fns-tz";
import {
  Calendar,
  CalendarDataResource,
  CalendarDataResourceEvent,
  ResourceSlotSelection,
  ResourceSlot,
  DateCalendarSelector,
  PopoverButton,
  Icons,
  ReactSelect,
  TagList,
  Button,
  CalendarView,
  Avatar,
  Text,
  useToggle,
} from "@pushpress/shared-components";
import { ResourceLabelContentArg } from "@fullcalendar/resource";
import { env } from "../../../env";
import { AppContext } from "../../../context/app";
import useIsMobile from "../../../hooks/useIsMobile";
import {
  defaultToastOptions,
  extractError,
  getInitials,
  joinStrings,
} from "../../../utils/misc";
import {
  PostRescheduleAppointmentPayload,
  postRescheduleAppointment,
} from "../../../apis/appointmentApi";
import AppointmentsProvider, {
  useAppointments,
} from "../AppointmentsProvider/AppointmentsProvider";
import {
  AppointmentFragment,
  StaffFragment,
  useGetAppointmentTypesForAppointmentsQuery,
  useGetStaffBusyTimesQuery,
  useGetStaffForAppointmentsQuery,
  useGetStaffWorkingHoursQuery,
  useWorkingHoursReminderMutation,
} from "./AppointmentsStart-generated.hooks";
import AppointmentsFilter from "./AppointmentsFilter";
import AppointmentHeader from "./AppointmentHeader";
import {
  AppointmentCalendarView,
  ListViewOption,
  ViewOption,
  appointmentCalendarViews,
  convertToResourceSlot,
  convertToTimezone,
  filterAppointmentsItems,
  filterAvailableStaff,
  getAppointmentsFilterTags,
  getViewRange,
  listViewOptions,
  viewOptions,
} from "./AppointmentsStart.utils";
import ListEvent from "./ListEvent";
import { AppointmentFilter } from "../Appointments.types";
import { sortByFirstName } from "../../../utils/sortUtils";
import usePersistentString from "./usePersistentString";
import useFilter from "./useFilter";
import { getAppointmentStatusName } from "../AppointmentsUtils";
import SetupAvailabilitySection from "./SetupAvailabilitySection/SetupAvailabilitySection";
import SetupAvailabilityModal from "./SetupAvailabilityModal/SetupAvailabilityModal";
import styles from "./AppointmentsStart.module.scss";
import { useBatchAppointmentScheduleQuery } from "./AppointmentsStart.hooks";

export const AppointmentContent = () => {
  // hooks

  const { client } = useContext(AppContext);
  const { showScheduleModal, showBookAppointmentsDrawer } = useAppointments();
  const isMobile = useIsMobile();
  const timezone = client.timezone;

  // states

  const [view, setView] = usePersistentString<AppointmentCalendarView>({
    storageKey: "appointments-start-view",
    defaultValue: "week",
    validate: (value) => appointmentCalendarViews.includes(value),
  });

  const [listView, setListView] = usePersistentString<CalendarView>({
    storageKey: "appointments-start-list-view",
    defaultValue: "listDay",
  });

  const [date, setDate] = useState(new Date());

  const { filter, setFilter, filterButtonProps } = useFilter<AppointmentFilter>(
    {
      defaultValue: {},
      storageKey: "appointments-start-filter",
    },
  );

  const [singleStaffUuidSelected, setSingleStaffUuidSelected] =
    useState<string>();

  const [pendingStaffUuidSelected, setPendingStaffUuidSelected] =
    useState<string>();

  const [setupAvailabilityVisible, setupAvailabilityToggle] = useToggle(false);

  const [remindersSent, setRemindersSent] = useState<Record<string, boolean>>(
    {},
  );

  // api

  const { startDate, endDate } = getViewRange(date, view, listView);

  const {
    data: appointmentsData,
    isLoading: scheduleLoading,
    refetch: appointmentsRefetch,
  } = useBatchAppointmentScheduleQuery(
    {
      from: zonedTimeToUtc(startDate, "UTC").toISOString(),
      to: zonedTimeToUtc(endDate, "UTC").toISOString(),
    },
    {
      onError(error) {
        toast.error(extractError(error), defaultToastOptions);
      },
    },
  );

  const { data: { getStaff: staff = [] } = {}, loading: staffLoading } =
    useGetStaffForAppointmentsQuery({
      fetchPolicy: "no-cache",
      onError(error) {
        toast.error(extractError(error), defaultToastOptions);
      },
    });

  const staffUuids =
    view === "singleStaff" && singleStaffUuidSelected
      ? [singleStaffUuidSelected]
      : view === "multiStaff" && filter.staff?.length
      ? filter.staff.map((s) => s.userUuid)
      : undefined;

  const skipBusyTimes =
    (view !== "multiStaff" && view !== "singleStaff") ||
    (view === "singleStaff" && !singleStaffUuidSelected);

  const {
    data: { getStaff: staffBusyTimes } = {},
    loading: staffBusyTimesLoading,
  } = useGetStaffBusyTimesQuery({
    variables: {
      input: {
        staffUuids,
      },
      busyTimesFilter: {
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
      },
    },
    skip: skipBusyTimes,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    onError(error) {
      toast.error(extractError(error), defaultToastOptions);
    },
  });

  const {
    data: { getStaff: staffWorkingHours } = {},
    loading: staffWorkingHoursLoading,
  } = useGetStaffWorkingHoursQuery({
    fetchPolicy: "no-cache",
    onError(error) {
      toast.error(extractError(error), defaultToastOptions);
    },
  });

  const { data: { getAppointmentTypes: appointmentTypes = [] } = {} } =
    useGetAppointmentTypesForAppointmentsQuery({
      fetchPolicy: "no-cache",
      onError(error) {
        toast.error(extractError(error), defaultToastOptions);
      },
    });

  const [sendReminderMutate, { loading: sendingReminder }] =
    useWorkingHoursReminderMutation();

  const postRescheduleMutation = useMutation(postRescheduleAppointment);

  //  callbacks

  const handleSelectViewChange = (option: ViewOption) => {
    setView(option.value);
  };

  const handleSelectListViewChange = (option: ListViewOption) => {
    setListView(option.value);
  };

  const handleSelectStaffChange = (option: StaffFragment) => {
    setSingleStaffUuidSelected(option.userUuid);
  };

  const handleEventClick = (event: AppointmentFragment) => {
    showScheduleModal(event.uuid, {
      onSuccess: () => appointmentsRefetch(),
    });
  };

  const handleDayClick = (value: Date) => {
    setDate(value);
    setView("day");
  };

  const handleDaySelect = (value: Date) => {
    showBookAppointmentsDrawer(
      { date: value },
      { onSuccess: () => appointmentsRefetch() },
    );
  };

  const handleEventChange = async (event: CalendarDataResourceEvent) => {
    const timeZone = client.timezone;
    const startTime = event.start;
    const endTime = event.end;
    const payload: PostRescheduleAppointmentPayload = {
      startTime,
      endTime,
      startTimeZone: timeZone,
      endTimeZone: timeZone,
    };
    try {
      await postRescheduleMutation.mutateAsync({
        clientUuid: client.uuid,
        appointmentUuid: event.id,
        payload,
      });
      toast.success("Appointment rescheduled", defaultToastOptions);
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const handleSlotsSelect = (selection: ResourceSlotSelection) => {
    const { start, resourceId } = selection;
    showBookAppointmentsDrawer(
      { date: start, staffUuid: resourceId },
      { onSuccess: () => appointmentsRefetch() },
    );
  };

  const handleCreateAppointmentClick = () => {
    showBookAppointmentsDrawer({}, { onSuccess: () => appointmentsRefetch() });
  };

  const handleSendReminder = async (userUuid: string) => {
    try {
      const { data } = await sendReminderMutate({
        variables: {
          userUuid,
        },
      });
      if (!data?.workingHoursReminder?.success) {
        throw new Error("Failed to send reminder");
      }
      setRemindersSent((prev) => ({ ...prev, [userUuid]: true }));
      const selectedStaff = staff.find((s) => s.userUuid === userUuid);
      toast.success(
        `An email has been sent to ${joinStrings(
          selectedStaff?.firstName,
          selectedStaff?.lastName,
        )}`,
        defaultToastOptions,
      );
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setupAvailabilityToggle.off();
    }
  };

  const eventAccessor = (
    event: AppointmentFragment,
  ): CalendarDataResourceEvent => {
    const color = event.appointmentType.color;
    const colorProps: {
      color: string;
      backgroundColor?: string;
      borderColor?: string;
      textColor?: string;
    } = {
      color,
    };
    const reverse = event.eventStatus === "CANCELLED" || !!event.unpaidReason;
    const statusName = getAppointmentStatusName(event);
    const isUnpaid =
      statusName === "UNPAID LATE CANCEL" || statusName === "UNPAID NO SHOW";
    if (reverse) {
      colorProps.backgroundColor = "#fff";
      colorProps.borderColor = color;
      colorProps.textColor = color;
    }
    const className: string[] = [];
    if (reverse) {
      className.push(styles.strikethrough);
    }
    if (isUnpaid && view !== "list" && view !== "month") {
      className.push(styles.boxAlert);
    } else if (isUnpaid && view === "month") {
      className.push(styles.bulletAlert);
    }

    return {
      ...colorProps,
      id: event.uuid,
      title: joinStrings(event.user?.firstName, event.user?.lastName),
      start: convertToTimezone(new Date(event.startTime), timezone),
      end: convertToTimezone(new Date(event.endTime), timezone),
      coach: event.staff
        ? `${event.staff.firstName} ${event.staff.lastName}`
        : undefined,
      resourceId: event.staff?.uuid,
      className,
      startEditable: event.eventStatus !== "CANCELLED",
    };
  };

  const resourceAccessor = (staff: StaffFragment): CalendarDataResource => {
    return {
      id: staff.userUuid,
      title: joinStrings(staff?.firstName, staff?.lastName),
      image: staff.primaryImage!,
    };
  };

  const renderEvent = (appointment: AppointmentFragment) => {
    return <ListEvent appointment={appointment} timezone={timezone} />;
  };

  const staffPendingSetupMap = useMemo<Record<string, boolean>>(() => {
    if (!staffWorkingHours) {
      return {};
    }
    return staffWorkingHours.reduce<Record<string, boolean>>((prev, cur) => {
      return {
        ...prev,
        [cur.userUuid]: cur.workingHours.length === 0,
      };
    }, {});
  }, [staffWorkingHours]);

  const openSetupAvailabilityModal = (userUuid: string) => {
    setPendingStaffUuidSelected(userUuid);
    setupAvailabilityToggle.on();
  };

  const renderResourceLabel = (arg: ResourceLabelContentArg) => {
    const { resource } = arg;
    const image = resource.extendedProps?.image;
    const initials = getInitials(resource.title);
    const pending = staffPendingSetupMap[resource.id];
    const sent = remindersSent[resource.id];
    const alertClassName = `${styles.resourceAlert} ${sent ? styles.sent : ""}`;
    const RootElem = pending ? "button" : "div";
    return (
      <RootElem
        className={`${styles.resourceLabel} ${
          pending ? styles.resourceLabelClickable : undefined
        }`}
        onClick={
          pending ? () => openSetupAvailabilityModal(resource.id) : undefined
        }
      >
        <Avatar size="medium">
          {image ? <img src={image} alt="Staff" /> : initials}
        </Avatar>
        <Text variant="body-lg" color="wolf-800">
          {resource.title}
        </Text>
        {pending && (
          <span className={alertClassName} aria-label="Pending">
            !
          </span>
        )}
      </RootElem>
    );
  };

  // memos

  const data = useMemo(() => {
    let nextFilter = filter;
    if (view === "singleStaff") {
      nextFilter = {
        ...filter,
        staff: [{ userUuid: singleStaffUuidSelected } as StaffFragment],
      };
    }
    return filterAppointmentsItems(appointmentsData || [], nextFilter);
  }, [appointmentsData, filter, view, singleStaffUuidSelected]);

  const availableStaff = useMemo(() => {
    const available = filterAvailableStaff(staff, appointmentTypes);
    return available.sort(sortByFirstName);
  }, [appointmentTypes, staff]);

  const resources = useMemo(() => {
    const staffOrSelectedStaff = (
      filter?.staff?.length ? filter.staff : staff || []
    ) as StaffFragment[];
    const available = filterAvailableStaff(
      staffOrSelectedStaff,
      appointmentTypes,
    );
    return available.sort(sortByFirstName);
  }, [appointmentTypes, staff, filter]);

  const blockedSlots = useMemo<ResourceSlot[]>(() => {
    return convertToResourceSlot(staffBusyTimes, timezone);
  }, [staffBusyTimes, timezone]);

  const filterTags = useMemo(() => {
    const ignoreStaff = view === "singleStaff";
    const tags = getAppointmentsFilterTags(filter, ignoreStaff);
    return tags.map((tag) => ({
      label: tag.label,
      onClose() {
        setFilter(tag.onClose());
      },
    }));
  }, [filter, view, setFilter]);

  const setupAvailabilityMessage = useMemo(() => {
    let uuid = "";
    if (view === "multiStaff") {
      uuid = pendingStaffUuidSelected;
    } else if (view === "singleStaff") {
      uuid = singleStaffUuidSelected;
    }
    const name = staff.find((s) => s.userUuid === uuid)?.firstName;
    return `${name} has not setup their availability yet.`;
  }, [staff, singleStaffUuidSelected, pendingStaffUuidSelected, view]);

  // select the first staff when load
  useEffect(() => {
    if (availableStaff.length > 0 && !singleStaffUuidSelected) {
      setSingleStaffUuidSelected(availableStaff[0].userUuid);
    }
  }, [availableStaff, singleStaffUuidSelected]);

  const loading =
    scheduleLoading ||
    staffLoading ||
    staffBusyTimesLoading ||
    staffWorkingHoursLoading;
  const stickyHeadersOffset = isMobile ? 0 : 67;
  const viewOption = viewOptions.find((o) => o.value === view)!;
  const staffOption = availableStaff.find(
    (o) => o.userUuid === singleStaffUuidSelected,
  );
  const calendarView = viewOption.view === "list" ? listView : viewOption.view;
  const listViewOption = listViewOptions.find((o) => o.value === listView)!;
  const shouldShowSetupAvailability =
    view === "singleStaff" && staffPendingSetupMap[singleStaffUuidSelected];
  const reminderDisabled = remindersSent[singleStaffUuidSelected];

  return (
    <>
      <div className={styles.pageHeader}>
        <AppointmentHeader onBookClick={handleCreateAppointmentClick} />
      </div>
      <div className={styles.calendarHeader}>
        <TagList
          tags={filterTags}
          closable
          lineHeight="34px"
          className={styles.tagList}
          startAdornment={
            <div className={styles.tagsAdornment}>
              <div className={styles.dateSelector}>
                <DateCalendarSelector
                  date={date}
                  view={calendarView}
                  onChange={setDate}
                />
              </div>
              <div className={styles.selects}>
                <ReactSelect
                  value={viewOption}
                  enableOptionIcon
                  enableSingleValueIcon
                  options={viewOptions}
                  className={styles.viewSelect}
                  isSearchable={false}
                  aria-label="Select the view"
                  onChange={handleSelectViewChange}
                />
                {view === "singleStaff" && (
                  <ReactSelect
                    value={staffOption}
                    enableOptionIcon
                    enableSingleValueIcon
                    options={availableStaff}
                    className={styles.staffSelect}
                    aria-label="Select the staff"
                    onChange={handleSelectStaffChange}
                    getOptionValue={(option: StaffFragment) => option.userUuid}
                    getOptionLabel={(option: StaffFragment) =>
                      joinStrings(option.firstName, option.lastName)
                    }
                  />
                )}
                {view === "list" && (
                  <ReactSelect
                    value={listViewOption}
                    options={listViewOptions}
                    className={styles.listSelect}
                    aria-label="Select the list view"
                    isSearchable={false}
                    onChange={handleSelectListViewChange}
                  />
                )}
              </div>
              <div className={styles.buttons}>
                <PopoverButton
                  buttonType="secondary"
                  text="Filter"
                  icon={Icons.Filter}
                  iconLocation="left"
                >
                  {({ onClose }) => (
                    <AppointmentsFilter
                      filter={filter}
                      staff={availableStaff}
                      appointmentTypes={appointmentTypes}
                      hideStaff={view === "singleStaff"}
                      onFilterChange={(value) => {
                        setFilter(value);
                        onClose();
                      }}
                      onClear={() => {
                        setFilter({});
                        onClose();
                      }}
                    />
                  )}
                </PopoverButton>
                <Button {...filterButtonProps} />
              </div>
            </div>
          }
        />
      </div>
      {shouldShowSetupAvailability && (
        <SetupAvailabilitySection
          message={setupAvailabilityMessage}
          userUuid={singleStaffUuidSelected}
          reminderDisabled={reminderDisabled}
          loading={sendingReminder}
          onSendClick={handleSendReminder}
        />
      )}
      <div className={styles.calendarContent}>
        <Calendar
          view={calendarView}
          date={date}
          timezone="UTC"
          stickyHeaders
          stickyHeadersOffset={stickyHeadersOffset}
          stickyTimeColumn
          minColumnWidth={160}
          events={data}
          loading={loading}
          blockedSlots={blockedSlots}
          nowIndicator
          nowTimezone={timezone}
          slotsSelectable
          resources={resources}
          schedulerLicenseKey={env.FULL_CALENDAR_LICENSE_KEY}
          eventStartEditable
          maxSelection={60}
          hideAllDaySlot
          resourceAccessor={resourceAccessor}
          eventAccessor={eventAccessor}
          renderEvent={view === "list" ? renderEvent : undefined}
          renderResourceLabel={renderResourceLabel}
          onEventClick={handleEventClick}
          onEventChange={handleEventChange}
          onSlotsSelect={handleSlotsSelect}
          onDayClick={handleDayClick}
          onDaySelect={handleDaySelect}
        />
      </div>
      <SetupAvailabilityModal
        userUuid={pendingStaffUuidSelected}
        message={setupAvailabilityMessage}
        visible={setupAvailabilityVisible}
        loading={sendingReminder}
        onClose={setupAvailabilityToggle.off}
        onSendClick={handleSendReminder}
      />
    </>
  );
};

function AppointmentStart() {
  return (
    <>
      <Helmet>
        <title>Appointments</title>
      </Helmet>
      <AppointmentsProvider>
        <AppointmentContent />
      </AppointmentsProvider>
    </>
  );
}

export default AppointmentStart;
